import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    acceptAllConsent,
    DEFAULT_CONSENT,
    rejectOptionalConsent,
} from "../../utils/cookieConsent";
import { useCookieConsent } from "../../contexts/CookieConsentContext";
import "./CookieConsent.css";

function ConsentToggle({ id, label, description, checked, disabled, onChange }) {
    return (
        <div className="cookie-consent-toggle">
            <div className="cookie-consent-toggle-text">
                <label htmlFor={id} className="cookie-consent-toggle-label">
                    {label}
                </label>
                <p className="cookie-consent-toggle-desc">{description}</p>
            </div>
            <label className="cookie-consent-switch">
                <input
                    id={id}
                    type="checkbox"
                    checked={checked}
                    disabled={disabled}
                    onChange={(event) => onChange(event.target.checked)}
                />
                <span className="cookie-consent-switch-slider" />
            </label>
        </div>
    );
}

function CookieConsent() {
    const { consent, bannerVisible, settingsOpen, setSettingsOpen, persistConsent } = useCookieConsent();
    const [draft, setDraft] = useState({
        analytics: consent?.analytics ?? DEFAULT_CONSENT.analytics,
        advertising: consent?.advertising ?? DEFAULT_CONSENT.advertising,
    });

    useEffect(() => {
        if (settingsOpen) {
            setDraft({
                analytics: consent?.analytics ?? DEFAULT_CONSENT.analytics,
                advertising: consent?.advertising ?? DEFAULT_CONSENT.advertising,
            });
        }
    }, [settingsOpen, consent]);

    if (!bannerVisible && !settingsOpen) {
        return null;
    }

    function handleSavePreferences() {
        persistConsent({
            necessary: true,
            analytics: draft.analytics,
            advertising: draft.advertising,
        });
    }

    return (
        <>
            {settingsOpen && (
                <div
                    className="cookie-consent-backdrop"
                    role="presentation"
                    onClick={() => setSettingsOpen(false)}
                />
            )}

            <div
                className={`cookie-consent-panel${settingsOpen ? " cookie-consent-panel--settings" : ""}`}
                role="dialog"
                aria-live="polite"
                aria-label="Cookie consent"
            >
                {settingsOpen ? (
                    <>
                        <h2 className="cookie-consent-title">Cookie preferences</h2>
                        <p className="cookie-consent-body">
                            Choose which optional cookies we can use. Necessary cookies keep the site working
                            (for example, your sign-in session and saved draft year).
                        </p>

                        <div className="cookie-consent-toggles">
                            <ConsentToggle
                                id="cookie-necessary"
                                label="Necessary"
                                description="Required for core site features. Always enabled."
                                checked
                                disabled
                                onChange={() => {}}
                            />
                            <ConsentToggle
                                id="cookie-analytics"
                                label="Analytics"
                                description="Helps us understand how the site is used via Google Tag Manager."
                                checked={draft.analytics}
                                onChange={(value) => setDraft((prev) => ({ ...prev, analytics: value }))}
                            />
                            <ConsentToggle
                                id="cookie-advertising"
                                label="Advertising"
                                description="Supports ads through Google AdSense."
                                checked={draft.advertising}
                                onChange={(value) => setDraft((prev) => ({ ...prev, advertising: value }))}
                            />
                        </div>

                        <div className="cookie-consent-actions">
                            <button type="button" className="cookie-consent-btn cookie-consent-btn--ghost" onClick={() => setSettingsOpen(false)}>
                                Cancel
                            </button>
                            <button type="button" className="cookie-consent-btn cookie-consent-btn--primary" onClick={handleSavePreferences}>
                                Save preferences
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <h2 className="cookie-consent-title">We use cookies</h2>
                        <p className="cookie-consent-body">
                            We use necessary cookies to run Hockey Pipeline, and optional cookies for analytics and
                            advertising. You can accept all, reject optional cookies, or manage your choices.{" "}
                            <Link to="/terms-of-service" className="cookie-consent-link">
                                Learn more
                            </Link>
                        </p>
                        <div className="cookie-consent-actions">
                            <button
                                type="button"
                                className="cookie-consent-btn cookie-consent-btn--ghost"
                                onClick={() => setSettingsOpen(true)}
                            >
                                Manage preferences
                            </button>
                            <button
                                type="button"
                                className="cookie-consent-btn cookie-consent-btn--secondary"
                                onClick={() => rejectOptionalConsent()}
                            >
                                Reject optional
                            </button>
                            <button
                                type="button"
                                className="cookie-consent-btn cookie-consent-btn--primary"
                                onClick={() => acceptAllConsent()}
                            >
                                Accept all
                            </button>
                        </div>
                    </>
                )}
            </div>
        </>
    );
}

export default CookieConsent;
